// @flow

import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { fetchQuery, graphql } from 'relay-runtime';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { i18n } from 'shared/utils';
import Icon from 'shared/components/common/Icon';

const ScannerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f7ff;
  border: 2px solid #2563eb;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 16px;
`;

const ScannerIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const BarcodeIconSvg = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 5v-2h4" />
    <path d="M17 3h4v2" />
    <path d="M21 19v2h-4" />
    <path d="M7 21h-4v-2" />
    <line x1="7" y1="8" x2="7" y2="16" />
    <line x1="11" y1="8" x2="11" y2="16" />
    <line x1="15" y1="8" x2="15" y2="16" />
    <line x1="9" y1="8" x2="9" y2="16" strokeWidth="1" />
    <line x1="13" y1="8" x2="13" y2="16" strokeWidth="1" />
    <line x1="17" y1="8" x2="17" y2="16" />
  </svg>
);

const ScanInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  outline: none;
  font-family: 'Courier New', monospace;

  &::placeholder {
    color: #93c5fd;
    font-weight: 400;
    font-family: inherit;
  }
`;

const ResultBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.type) {
      case 'ITEM':
        return '#dcfce7';
      case 'LOT':
        return '#fef3c7';
      case 'LOCATION':
        return '#dbeafe';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'ITEM':
        return '#166534';
      case 'LOT':
        return '#92400e';
      case 'LOCATION':
        return '#1e40af';
      default:
        return '#374151';
    }
  }};
`;

const NoResult = styled.span`
  font-size: 12px;
  color: #dc2626;
  font-weight: 500;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => (props.active ? '#22c55e' : '#d1d5db')};
  flex-shrink: 0;
`;

const BARCODE_LOOKUP_QUERY = graphql`
  query BarcodeScannerInputQuery($code: String!) {
    barcodeLookup(code: $code) {
      type
      id
      label
      code
      description
      quantity
      lotTracking
    }
  }
`;

type Props = {
  /** Called when a barcode is scanned and a result is found */
  onScan: (result: {
    type: 'ITEM' | 'LOT' | 'LOCATION',
    id: string,
    label: string,
    code: string,
    description: ?string,
    quantity: ?number,
    lotTracking: ?boolean,
  }) => void,
  /** Called when scan completes but no results found */
  onScanNotFound?: (code: string) => void,
  /** Placeholder text */
  placeholder?: string,
  /** Auto-focus on mount */
  autoFocus?: boolean,
  /** Filter results to specific types */
  filterType?: 'ITEM' | 'LOT' | 'LOCATION' | null,
};

type State = {
  inputValue: string,
  lastResult: ?Object,
  scanning: boolean,
  noResult: boolean,
};

/**
 * BarcodeScannerInput — a text input optimized for USB barcode scanners.
 *
 * USB scanners act as keyboard input devices: they "type" the barcode value
 * rapidly and then press Enter.
 *
 * This component:
 * 1. Accepts text input (scanner or manual)
 * 2. On Enter, queries the barcodeLookup GQL endpoint
 * 3. Calls onScan with the first matching result
 * 4. Clears input for the next scan
 */
class BarcodeScannerInput extends PureComponent<Props, State> {
  inputRef: ?HTMLInputElement = null;

  static defaultProps = {
    placeholder: 'Scan barcode or type code...',
    autoFocus: false,
    filterType: null,
    onScanNotFound: null,
  };

  state = {
    inputValue: '',
    lastResult: null,
    scanning: false,
    noResult: false,
  };

  componentDidMount() {
    if (this.props.autoFocus && this.inputRef) {
      this.inputRef.focus();
    }
  }

  handleChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({
      inputValue: e.target.value,
      noResult: false,
      lastResult: null,
    });
  };

  handleKeyDown = (e: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleLookup();
    }
  };

  handleLookup = async () => {
    const { inputValue } = this.state;
    const code = inputValue.trim();

    if (!code) return;

    this.setState({ scanning: true, noResult: false, lastResult: null });

    try {
      const data = await fetchQuery(relayEnvironment, BARCODE_LOOKUP_QUERY, {
        code,
      });

      let results = data.barcodeLookup || [];

      if (this.props.filterType) {
        results = results.filter(r => r.type === this.props.filterType);
      }

      if (results.length > 0) {
        const match = results[0];
        this.setState({
          lastResult: match,
          inputValue: '',
          scanning: false,
        });
        this.props.onScan(match);
      } else {
        this.setState({ noResult: true, scanning: false });
        if (this.props.onScanNotFound) {
          this.props.onScanNotFound(code);
        }
      }
    } catch (err) {
      this.setState({ scanning: false, noResult: true });
    }

    // Re-focus for next scan
    if (this.inputRef) {
      this.inputRef.focus();
    }
  };

  render() {
    const { placeholder } = this.props;
    const { inputValue, lastResult, scanning, noResult } = this.state;

    return (
      <ScannerWrapper>
        <ScannerIcon>
          <BarcodeIconSvg />
        </ScannerIcon>
        <StatusDot active={!scanning} />
        <ScanInput
          ref={ref => (this.inputRef = ref)}
          type="text"
          value={inputValue}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          placeholder={scanning ? i18n.t('Looking up...') : placeholder}
          disabled={scanning}
        />
        {lastResult && (
          <ResultBadge type={lastResult.type}>
            {lastResult.type}: {lastResult.label}
          </ResultBadge>
        )}
        {noResult && <NoResult>{i18n.t('Not found')}</NoResult>}
      </ScannerWrapper>
    );
  }
}

export default BarcodeScannerInput;
